import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, PauseCircle, RotateCcw, X } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const RETRY = {
  generate: (id) => http.post(`/kits/${id}/regenerate`, {}),
  page_check: (id) => http.post(`/kits/${id}/page-check`),
  rewrite_all: (id) => http.post(`/kits/${id}/style-fix-all`),
  fill: (id) => http.post(`/kits/${id}/fill-pages`),
  nightly: (id) => http.post(`/kits/${id}/fill-pages`),
  info: (id) => http.post(`/kits/${id}/standardize-info`),
  prices: (id) => http.post(`/kits/${id}/price-strip/scan`),
  original: (id) => http.post(`/kits/${id}/original-fill`),
  webcheck: (id) => http.post(`/kits/${id}/original-webcheck`),
};

const when = (at) => (at ? new Date(at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const Stop = ({ s, kitId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const Icon = s.paused ? PauseCircle : AlertTriangle;
  return (
    <div data-testid={`job-stop-${s.job}`} className={`flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm ${s.paused ? "border-amber-200 bg-amber-50/60" : "border-red-200 bg-red-50/60"}`}>
      <Icon size={16} className={s.paused ? "text-amber-600" : "text-red-600"} />
      <b className="text-[#2B2A26]">{s.label} {s.paused ? "paused" : "stopped"}</b>
      <span data-testid={`job-stop-reason-${s.job}`} className="text-stone-700">{s.reason}</span>
      {s.at && <span className="text-xs text-stone-400">{when(s.at)}</span>}
      <div className="ml-auto flex gap-2">
        {s.retry && !s.paused && RETRY[s.job] && (
          <button data-testid={`job-stop-retry-${s.job}`} disabled={busy} onClick={() => act(() => RETRY[s.job](kitId), `${s.label} started again`)}
            className="inline-flex items-center gap-1 rounded-sm bg-[#2B2A26] px-3 py-1 text-xs font-bold text-white hover:bg-black disabled:opacity-50"><RotateCcw size={12} /> Try again</button>
        )}
        <button data-testid={`job-stop-dismiss-${s.job}`} disabled={busy} onClick={() => act(() => http.post(`/kits/${kitId}/job-status/${s.job}/dismiss`), "Hidden")}
          className="inline-flex items-center gap-1 rounded-sm border border-stone-300 px-2 py-1 text-xs text-stone-600 hover:bg-white disabled:opacity-50"><X size={12} /> Dismiss</button>
      </div>
    </div>
  );
};

export const JobStops = ({ kit, reload }) => {
  const [stops, setStops] = useState([]);
  const load = useCallback(() => http.get(`/kits/${kit.id}/job-status`).then((r) => setStops(r.data.stops.filter((s) => s.job !== "generate"))).catch(() => {}), [kit.id]);
  useEffect(() => { load(); }, [load, kit.updated_at, kit.filling, kit.page_checking]);
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!stops.length) return null;
  return (
    <div data-testid="job-stops" className="mb-6 space-y-2">
      {stops.map((s) => <Stop key={s.job} s={s} kitId={kit.id} onDone={() => { load(); reload(); }} />)}
    </div>
  );
};

export const JobStopBadge = ({ k }) => {
  const s = k.stops?.[0];
  if (!s) return null;
  return (
    <div data-testid={`kit-stop-badge-${k.id}`} title={k.stops.map((x) => `${x.label}: ${x.reason}`).join("\n")}
      className={`mt-0.5 flex items-center gap-1 text-[11px] truncate ${s.paused ? "text-amber-700" : "text-red-600"}`}>
      {s.paused ? <PauseCircle size={11} /> : <AlertTriangle size={11} />}
      <span className="truncate">{s.label} {s.paused ? "paused" : "stopped"}: {s.reason}{k.stops.length > 1 ? ` (+${k.stops.length - 1} more)` : ""}</span>
    </div>
  );
};
